import React, { useCallback, useEffect, useRef, useState } from 'react';
import { mediaUrl } from '../utils/blogUtils';
import './PhotoGallery.css';

/**
 * @typedef {{ src: string, alt?: string, caption?: string }} PhotoLightboxItem
 */

export function usePhotoLightbox(items = []) {
  const [index, setIndex] = useState(null);

  const openAt = useCallback((itemIndex) => {
    setIndex(itemIndex);
  }, []);

  const close = useCallback(() => {
    setIndex(null);
  }, []);

  const goPrev = useCallback(() => {
    setIndex((current) => {
      if (current === null || items.length === 0) return current;
      return current > 0 ? current - 1 : items.length - 1;
    });
  }, [items.length]);

  const goNext = useCallback(() => {
    setIndex((current) => {
      if (current === null || items.length === 0) return current;
      return current < items.length - 1 ? current + 1 : 0;
    });
  }, [items.length]);

  return {
    isOpen: index !== null,
    index,
    openAt,
    close,
    goPrev,
    goNext,
  };
}

export function PhotoLightbox({
  items,
  index,
  onClose,
  onPrev,
  onNext,
}) {
  useEffect(() => {
    if (index === null) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') onPrev();
      if (event.key === 'ArrowRight') onNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [index, onClose, onPrev, onNext]);

  const touchStartX = useRef(null);

  const onTouchStart = useCallback((event) => {
    touchStartX.current = event.changedTouches[0]?.clientX ?? null;
  }, []);

  const onTouchEnd = useCallback((event) => {
    const start = touchStartX.current;
    if (start == null) return;
    const end = event.changedTouches[0]?.clientX ?? start;
    const delta = end - start;
    touchStartX.current = null;
    if (Math.abs(delta) < 50) return;
    if (delta > 0) onPrev();
    else onNext();
  }, [onPrev, onNext]);

  if (index === null || !items?.length) return null;

  const item = items[index];
  const src = mediaUrl(item.src);
  const showNav = items.length > 1;

  return (
    <div className="lightbox" onClick={onClose} role="dialog" aria-modal="true" aria-label="Enlarged photo">
      <button type="button" className="lightbox-close" onClick={onClose} aria-label="Close">
        ×
      </button>
      {showNav && (
        <button
          type="button"
          className="lightbox-nav lightbox-prev"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          aria-label="Previous photo"
        >
          ‹
        </button>
      )}
      <div
        className="lightbox-content"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img src={src} alt={item.alt || ''} />
        {(item.caption || showNav) && (
          <div className="lightbox-counter">
            {item.caption ? (
              <>
                <span className="lightbox-caption">{item.caption}</span>
                {showNav && <span className="lightbox-counter__sep"> · </span>}
              </>
            ) : null}
            {showNav && <span>{index + 1} / {items.length}</span>}
          </div>
        )}
      </div>
      {showNav && (
        <button
          type="button"
          className="lightbox-nav lightbox-next"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          aria-label="Next photo"
        >
          ›
        </button>
      )}
    </div>
  );
}

export function ClickablePhoto({
  src,
  alt,
  className,
  onClick,
  loading = 'lazy',
}) {
  return (
    <button
      type="button"
      className={`photo-lightbox-trigger${className ? ` ${className}` : ''}`}
      onClick={onClick}
      aria-label={alt ? `View full size: ${alt}` : 'View full size'}
    >
      <img src={mediaUrl(src)} alt={alt || ''} loading={loading} />
      <span className="photo-lightbox-trigger__hint" aria-hidden="true">Tap to enlarge</span>
    </button>
  );
}
